import React from "react";
import ChapterShell from "@/tutorial/ChapterShell";
import {
  Section,
  Step,
  AnnotatedScreen,
  TipBox,
  InfoBox,
} from "@/tutorial/components/TutorialComponents";

export default function Chapter1() {
  return (
    <ChapterShell id={1}>
      <Section id="section-1-1" eyebrow="1.1" title="Créer votre compte">
        <p>
          L'inscription prend moins d'une minute. Aucune donnée bancaire n'est
          demandée : l'accès au tutoriel et aux fonctions de base est gratuit
          pour la recherche et l'enseignement.
        </p>

        <AnnotatedScreen
          src="/tutorial/images/chapter1-signup.png"
          alt="Formulaire d'inscription annoté"
          annotations={[
            { x: 50, y: 18, label: "①", text: "Email professionnel — sert d'identifiant unique." },
            { x: 50, y: 35, label: "②", text: "Mot de passe (minimum 12 caractères recommandés)." },
            { x: 50, y: 52, label: "③", text: "Nom complet + organisation (université, média…)." },
            { x: 50, y: 70, label: "④", text: "Pays d'exercice — utilisé pour la devise par défaut." },
            { x: 50, y: 88, label: "⑤", text: "Bouton Créer mon compte — cookies httpOnly sécurisés." },
          ]}
        />

        <div className="space-y-4">
          <Step number={1} title="Entrez votre email professionnel">
            Préférez une adresse institutionnelle (université, média, ONG) pour
            faciliter la vérification ultérieure.
          </Step>
          <Step number={2} title="Choisissez un mot de passe fort">
            Longueur minimale : 12 caractères. Mixez majuscules, minuscules,
            chiffres et un symbole.
          </Step>
          <Step number={3} title="Renseignez votre nom et organisation">
            Le nom apparaît dans les rapports exportés (en-tête personnalisable
            dans les Paramètres).
          </Step>
          <Step number={4} title="Sélectionnez votre pays d'exercice">
            Le pays pré-remplit la devise et le format monétaire des nouveaux
            projets.
          </Step>
          <Step number={5} title="Cliquez sur « Créer mon compte »">
            Vous êtes automatiquement connecté et redirigé vers le tableau de
            bord.
          </Step>
        </div>
      </Section>

      <Section
        id="section-1-2"
        eyebrow="1.2"
        title="Découvrir l'interface principale"
      >
        <AnnotatedScreen
          src="/tutorial/images/chapter1-dashboard.png"
          alt="Tableau de bord principal annoté"
          annotations={[
            { x: 15, y: 6, label: "A", text: "Header : logo, raccourci aide [?] (F1), profil utilisateur." },
            { x: 10, y: 50, label: "B", text: "Sidebar : navigation entre Dashboard, Nouveau projet, Mes analyses, Rapports, Paramètres." },
            { x: 60, y: 40, label: "C", text: "Zone centrale : contenu de la page courante (KPIs, wizards, tableaux)." },
            { x: 92, y: 92, label: "D", text: "Bouton flottant « Made with Emergent » — environnement d'hébergement." },
          ]}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs font-bold">Zone A — Header</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Ouverture de l'aide, bascule thème clair/sombre, déconnexion.
            </p>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs font-bold">Zone B — Sidebar</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Cinq modules principaux de l'application.
            </p>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs font-bold">Zone C — Contenu</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Tableaux de bord, wizards d'analyse, visualisations.
            </p>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <div className="text-xs font-bold">Zone D — Environnement</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Indicateur Emergent, non-intrusif, masqué en mode présentation.
            </p>
          </div>
        </div>
      </Section>

      <Section
        id="section-1-3"
        eyebrow="1.3"
        title="Créer votre premier projet"
      >
        <Step number={1} title="Cliquez sur « Nouveau projet » dans la sidebar">
          Accède à l'assistant en 7 étapes.
        </Step>
        <Step number={2} title="Donnez un nom clair au projet">
          Exemple : « Budget général Mauritanie 2024 ». Ce nom apparaît dans les
          exports.
        </Step>
        <Step number={3} title="Sélectionnez le pays et l'année fiscale">
          Un projet = un pays × un exercice. Pour comparer deux années, créez
          deux projets distincts (voir chapitre 9).
        </Step>
        <Step number={4} title="Enregistrez et passez à l'étape d'import">
          Le projet est sauvegardé. Vous pouvez y revenir à tout moment depuis
          « Mes analyses ».
        </Step>

        <TipBox title="Astuce d'organisation">
          Créez un projet par pays <strong>et</strong> par année fiscale. Cela
          facilite les comparaisons intertemporelles et les exports archivables.
        </TipBox>

        <InfoBox title="Suppression en cascade">
          Supprimer un projet supprime aussi ses documents, analyses, rapports
          et liens de partage associés. L'action est irréversible.
        </InfoBox>
      </Section>
    </ChapterShell>
  );
}
